import React from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

// Import util modules
import { intlShape, injectIntl, FormattedMessage } from '../../../../util/reactIntl';
import { propTypes } from '../../../../util/types';
import { required } from '../../../../util/validators';

// Import shared components
import { Form, Button, FieldTextInput } from '../../../../components';

import css from './EditListingRulesForm.module.css';
import useListingAutosave from '../useListingAutosave';
import ListingAutosaveStatus from '../ListingAutosaveStatus';

// Show various error messages
const ErrorMessage = props => {
  const { fetchErrors } = props;
  const { updateListingError, createListingDraftError, showListingsError } = fetchErrors || {};
  const errorMessage = updateListingError ? (
    <FormattedMessage id="EditListingRulesForm.updateFailed" />
  ) : createListingDraftError ? (
    <FormattedMessage id="EditListingRulesForm.createListingDraftError" />
  ) : showListingsError ? (
    <FormattedMessage id="EditListingRulesForm.showListingFailed" />
  ) : null;

  if (errorMessage) {
    return <p className={css.error}>{errorMessage}</p>;
  }
  return null;
};

// Form that asks title, description, transaction process and unit type for pricing
// In addition, it asks about custom fields according to marketplace-custom-config.js
const EditListingRulesFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    render={formRenderProps => {
      const {
        className,
        disabled,
        ready,
        handleSubmit,
        intl,
        invalid,
        pristine,
        saveActionMsg,
        updated,
        updateInProgress,
        fetchErrors,
        values,
      } = formRenderProps;

      const classes = classNames(css.root, className);
      const submitReady = (updated && pristine) || ready;
      const autosave = useListingAutosave({
        values,
        dirty: !pristine,
        valid: !invalid,
        onSave: autosaveValues => props.onSubmit(autosaveValues, { isAutosave: true }),
        enabled: !!props.autosaveEnabled,
      });
      const submitInProgress = updateInProgress || autosave.isSaving;
      const submitDisabled = invalid || disabled || submitInProgress;

      return (
        <Form className={classes} onSubmit={autosave.handleManualSubmit(handleSubmit)}>
          <ErrorMessage fetchErrors={fetchErrors} />

          <FieldTextInput
            id="rules"
            name="rules"
            className={css.textField}
            type="text"
            label={intl.formatMessage({ id: 'EditListingRulesForm.rules' })}
            placeholder={intl.formatMessage({ id: 'EditListingFeaturesForm.rulesPlaceholder' })}
            // validate={required(
            //   intl.formatMessage({
            //     id: 'EditListingRulesForm.rulesRequired',
            //   })
            // )}
          />

          {props.autosaveEnabled ? (
            <ListingAutosaveStatus status={autosave.status} onRetry={autosave.retry} />
          ) : null}
          <Button
            className={css.submitButton}
            type="submit"
            inProgress={submitInProgress}
            disabled={submitDisabled}
            ready={submitReady}
          >
            {saveActionMsg}
          </Button>
        </Form>
      );
    }}
  />
);

EditListingRulesFormComponent.defaultProps = {
  className: null,
  formId: 'EditListingRulesForm',
  fetchErrors: null,
  hasExistingListingType: false,
  listingFieldsConfig: [],
};

EditListingRulesFormComponent.propTypes = {
  className: string,
  formId: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  onListingTypeChange: func.isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    createListingDraftError: propTypes.error,
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
};

export default compose(injectIntl)(EditListingRulesFormComponent);
